/**
 *
 * @description APPLE PAY 支付相关配置
 * @date 2024/01/03 15:31:33
 */
import { SERVER } from '@/request/config';
import { httpRequest } from '@/request';
import { browserPushUrl } from '@/utils';
import { message, Modal } from 'antd';
import { isObject, omit } from 'lodash';
import { encodeURIEncryptFE } from '@/utils/libs/JSEncrypt';
import { armsErrorSend } from '@/utils/libs/armsErrorSend';
import { feishuRobotSend } from '@/utils/feishuRobot';
import { PaymentTracker } from '@/utils/Tracker';
import { ORDER_STATE } from '@/utils/configs/state.config';

// 禁用地区
const DISABLE_AREA = ['AD', 'RU', 'BN', 'CN', 'ID', 'IN', 'PH', 'TH', 'TR'];

// 收款通道 - 地区映射
const CKO_CHANNEL_ID_MAP = {
  '欧洲通道': {
    area: [
      'AT',
      'BE',
      'BG',
      'CH',
      'CY',
      'CZ',
      'DE',
      'DK',
      'EE',
      'ES',
      'FI',
      'FR',
      'GB',
      'GR',
      'HR',
      'HU',
      'IE',
      'IS',
      'IT',
      'LI',
      'LT',
      'LU',
      'LV',
      'MC',
      'MT',
      'NL',
      'NO',
      'PL',
      'PT',
      'RO',
      'SE',
      'SI',
      'SK',
      'AE',
      'KW',
      'SA',
    ],
    id: 1,
  },
  '美国通道': {
    area: ['US'],
    id: 2,
  },
  '香港通道': {
    area: ['AU', 'BR', 'CA', 'HK', 'HK', 'IL', 'JP', 'KR', 'MO', 'MX', 'MY', 'NZ', 'SG', 'TW', 'VN'],
    id: 3,
  },
};

const STEP = {
  '创建会话': '创建会话',
  '唤醒支付': '唤醒支付',
  '验证商户': '验证商户',
  '用户授权': '用户授权',
  '发起支付': '发起支付',
};

const STATE = {
  '超时': -9,
  '异常': -2,
  '失败': -1,
  '处理中': 0,
  '成功': 1,
};

/**
 *
 * @description 判断是否支持AP
 * @param CountryCode {String} 国家/地区二字码
 * @return
 */
const enable = ({ CountryCode = '', fromGetIconList = false }) => {
  try {
    // 判断当前浏览器环境是否支持AP会话
    const ApplePaySession = !!window?.ApplePaySession;

    // 判断是否支持ApplePay，不判断钱包是否存在卡
    const canMakePayments = window?.ApplePaySession?.canMakePayments?.();

    // 判断是否为支持的地区
    const isEnableArea = !DISABLE_AREA.includes(CountryCode);

    const isEnableApplePay = ApplePaySession && canMakePayments;

    if (fromGetIconList) return isEnableArea;
    else return isEnableArea && isEnableApplePay;
  } catch (e) {
    console.error('[AP]：是否支持会话判断异常', e);
    return false;
  }
};

/**
 *
 * @description 商户验证, 后端发起验证
 *              - domainName 必须在Apple开发者账号后台完成验证,并且需要与当前发起支付的域名一致
 *              - merchantIdentifier 后端维护在Nacos, 正式 & 沙盒各一个
 * @param {String} appleUrl SDK返回的AppleUrlGateway
 * @return {Promise}
 */
const validateApplePaySession = ({ appleUrl }) => {
  return httpRequest({
    url: SERVER.API_SERVER_PAYMENT + '/checkout/validateApplePaySession',
    method: 'POST',
    data: {
      appleUrl: appleUrl,
      domainName: window?.location?.hostname, // 当前前端域名，需要在Apple开发平台完成domain验证；
      displayName: 'Insta360',
    },
  });
};

/** 根据当前订单会话地区分配通道，由业务进行确定 */
const getPaymentChannelId = () => {
  const { countryCode } = window.InstaApplePaySession;

  for (const channelKey in CKO_CHANNEL_ID_MAP) {
    if (CKO_CHANNEL_ID_MAP.hasOwnProperty(channelKey)) {
      const channel = CKO_CHANNEL_ID_MAP[channelKey];
      if (channel.area.includes(countryCode)) {
        return channel.id;
      }
    }
  }

  feishuRobotSend({ text: [`Apple Pay 通道未配置 - ${countryCode}，默认走香港通道`], at: ['yhq'] });

  return CKO_CHANNEL_ID_MAP['香港通道'].id;
};

/**
 *
 * @description Apple Pay TokenData 发起CKO 支付
 * @param {Object} paymentData Apple Pay TokenData
 * @param {Number} OrderId 订单id
 * @param {Number} payMode 支付模式是订阅支付（2） 还是普通支付（1）
 * @return {Promise}
 */
const toApplePay = ({ paymentData, orderNumber, payMode }) => {
  return httpRequest({
    url: SERVER.API_SERVER_PAYMENT + '/checkout/ckoApplePay',
    method: 'POST',
    data: {
      orderNumber,
      paymentChannelId: getPaymentChannelId(), // （支付通道id）
      payMode,
      innerData: {
        data: paymentData.data, // （卡信息）
        signature: paymentData.signature, // （签名），
        publicKeyHash: paymentData.header.publicKeyHash, // （公钥hash），
        ephemeralPublicKey: paymentData.header.ephemeralPublicKey, // （临时公钥）
        transactionId: paymentData.header.transactionId, // （交易号），
        version: paymentData.version, // （算法版本）
      },
    },
  });
};

/**
 *
 * @description 记录关键步骤的状态
 * @param {String} step 当前步骤[STEP]
 * @param {Number} state 当前步骤状态[STATE]
 * @param {Object} msg 额外要console的信息
 * @return
 */
const recordStep = (step, state = 0) => {
  try {
    if (!isObject(window.InstaApplePaySession)) return;

    let payStepGroup = Array.isArray(window.InstaApplePaySession.payStepGroup)
      ? window.InstaApplePaySession.payStepGroup
      : [];
    payStepGroup.push({ step, state });

    // 记录支付所有步骤, STEP
    window.InstaApplePaySession.payStepGroup = payStepGroup;
    // 记录当前步骤，step
    window.InstaApplePaySession.payStep = step;
    // 记录当前步骤状态，STATE
    window.InstaApplePaySession.payStepState = state;
    // 记录当前步骤状态，STATE
    window.InstaApplePaySession.payStepStateLocale = Object.keys(STATE).find((key) => STATE[key] === state);

    // const stepRecordLog = [
    //   {
    //     '当前步骤 - 状态': `${step} - ${window.InstaApplePaySession.payStepStateLocale}`,
    //     '所有步骤': payStepGroup.map((o) => `${o.step} ${o.state}`).join(' || '),
    //   },
    // ];

    PaymentTracker.applePayTracker({
      action: step,
      label: window.InstaApplePaySession.payStepStateLocale,
    });

    // console.table(stepRecordLog);
    // console.log('[AP]: 当前步骤额外信息', msg);
  } catch (e) {
    console.error('[AP]: 记录支付步骤异常', e);
  }
};

/**
 *
 * @description 支付catch异常处理，创建会话/唤醒支付过程中任何catch error
 * @param {Object} orderState Mobx.Order
 * @param {Object} catchError Catch.Error
 * @param {Object} catchErrorData 额外要打印的data信息
 * @param {String} from 从 requestSession || wakeUpPayment 步骤抛出的异常
 */
const handleException = ({
  isOrderConfirmPage,
  orderState,
  orderNumber = '',
  catchError,
  catchErrorData = {},
  from,
  LANG,
}) => {
  try {
    const { countryCode } = window?.InstaApplePaySession || {};
    orderState.setStateValue('createOrderLoading', false);
    orderState.setStateValue('_beingPaid', false);

    const data = {
      msg: from === 'requestSession' ? '创建会话异常' : '支付处理异常',
      errorMsg: catchError?.message,
      ...catchErrorData,
      isOrderConfirmPage,
    };

    armsErrorSend(catchError, { filename: 'Apple Pay Exception', data });

    feishuRobotSend({
      text: [`Apple Pay 支付异常 - ${countryCode}`, `response：${JSON.stringify(data)}`],
      at: ['yhq'],
    });

    message.info(LANG['payments.methods.applePay_exception']);

    PaymentTracker.applePayTracker({
      action: from,
      label: 'exception',
    });

    window?.InstaApplePaySession?.abort?.();

    if (isOrderConfirmPage && from === 'wakeUpPayment') {
      browserPushUrl(`/order/result/${encodeURIEncryptFE(orderNumber)}`);
    }
  } catch (e) {
    message.info(LANG['payments.methods.applePay_exception']);
    armsErrorSend(e, {
      filename: 'Apple Pay Exception',
      data: { msg: '支付异常处理异常', errorMsg: e?.message },
    });
  }
};

/**
 *
 * @description 支付取消处理，无法区分是系统取消还是用户触发取消
 *
 */
const handleCancel = ({ isOrderConfirmPage, orderNumber, orderState, from, event }) => {
  // console.log('[AP]. 取消支付', window.InstaApplePaySession);

  let sendARMS = false;
  let sendFeiShu = false;

  const orderRePayUrl = `/order/pay/${encodeURIEncryptFE(orderNumber)}`;
  const orderResultUrl = `/order/result/${encodeURIEncryptFE(orderNumber)}`;

  try {
    const { payStep, payStepState, payStepStateLocale, LANG, countryCode } = window.InstaApplePaySession;

    orderState.setStateValue('createOrderLoading', false);
    orderState.setStateValue('_beingPaid', false);

    const ErrMsgData = {
      payStep,
      payStepStateLocale,
      orderNumber,
      from,
    };

    switch (true) {
      case payStep === STEP.验证商户 && payStepState === STATE.失败:
        sendARMS = true;
        sendFeiShu = true;
        if (isOrderConfirmPage) {
          browserPushUrl(orderResultUrl);
        }
        if (!isOrderConfirmPage) {
          message.info(LANG['payments.methods.applePay_exception']);
        }
        break;

      case payStep === STEP.验证商户 && payStepState === STATE.异常:
        // 因主动取消导致的异常, 如果是结账页则跳转二次支付页，否则留在当前页
        if (isOrderConfirmPage) {
          browserPushUrl(orderRePayUrl);
        }
        break;

      case payStep === STEP.发起支付 && payStepState === STATE.超时:
        sendFeiShu = true;
        if (isOrderConfirmPage) {
          browserPushUrl(orderResultUrl);
        }
        break;

      case payStep === STEP.发起支付 && payStepState === STATE.失败:
        sendARMS = true;
        sendFeiShu = true;
        browserPushUrl(orderResultUrl);
        break;

      case payStep === STEP.发起支付 && payStepState === STATE.异常:
        sendARMS = true;
        sendFeiShu = true;
        if (!isOrderConfirmPage) {
          Modal.info({
            title: LANG['payments.methods.applePay_error_modal_title'],
            content: LANG['payments.methods.applePay_error_modal_content'],
          });
        }
        if (isOrderConfirmPage) {
          browserPushUrl(orderResultUrl);
        }
        break;
      default:
        if (isOrderConfirmPage) {
          browserPushUrl(orderRePayUrl);
        }
    }

    // 飞书告警
    if (sendFeiShu) {
      // console.log('支付取消，飞书告警', ErrMsgData);
      feishuRobotSend({
        text: [`Apple Pay 取消 - ${countryCode}`, `response：${JSON.stringify(ErrMsgData)}`],
        at: ['yhq'],
      });
    }

    // ARMS日志上报
    if (sendARMS) {
      // console.log('支付取消，ARMS日志上报', ErrMsgData);
      armsErrorSend('', {
        filename: 'Apple Pay Cancel Handle',
        data: { msg: 'Apple Pay 取消', ...ErrMsgData },
      });
    }

    window.InstaApplePaySession?.abort?.();
  } catch (e) {
    console.log(event);
    console.error(event);
    // console.error('[AP]: 取消支付异常', e.message);
    armsErrorSend(e, {
      filename: 'Apple Pay Error',
      data: {
        msg: '支付取消处理异常',
        'window.InstaApplePaySession': omit(window.InstaApplePaySession, 'LANG'),
        event,
      },
    });
  }
};

/**
 * 封装request 及 唤醒ApplePay支付步骤分开，以解决下单过程因异步数据校验导致用户交互触发会话校验失败。
 * @description 唤醒ApplePay支付，后续商户验证、授权及支付；
 *
 * @link https://developer.apple.com/documentation/apple_pay_on_the_web/apple_pay_js_api/creating_an_apple_pay_session
 * @date 2024/01/03 19:25:53
 */
const wakeUpPayment = ({ isOrderConfirmPage, orderNumber, orderState, LANG, payMode }) => {
  const session = window.InstaApplePaySession;

  try {
    if (!session) {
      recordStep(STEP.唤醒支付, STATE.失败);
      throw new Error('会话不存在');
    }

    // 创建会话后调用begin显示付款单
    session.begin();
    recordStep(STEP.唤醒支付, STATE.成功, session);

    // 2. 发起商户验证
    session.onvalidatemerchant = async (event) => {
      recordStep(STEP.验证商户, STATE.处理中, event);

      const theValidationURL = event.validationURL;

      validateApplePaySession({
        appleUrl: theValidationURL,
      })
        .then((res) => {
          const isSameDomain = res?.data?.domainName === window.location.hostname;
          // 如果已经取消了，则不继续完成验证，否则会抛异常；
          // 先记录状态，再执行，cancel方法依赖状态
          if (isSameDomain) {
            recordStep(STEP.验证商户, STATE.成功, { res });
            session.completeMerchantValidation(res.data);
          } else {
            recordStep(STEP.验证商户, STATE.失败, { res });
            session.oncancel();
          }
        })
        .catch((err) => {
          recordStep(STEP.验证商户, STATE.异常, { err });
          session.oncancel();
        });
    };

    // 3. 用户支付授权，并发起支付，授权后有30s超时，若未完成会话会关闭，并自动取消
    session.onpaymentauthorized = async (event) => {
      const paymentToken = event.payment.token;

      recordStep(STEP.用户授权, STATE.成功, paymentToken);
      session.counter = 1; // 30s支付倒计时
      session.timer = null; // 计时器
      clearInterval(session.timer);

      session.timer = setInterval(() => {
        console.log('当前计时：', session.counter);
        if (session.counter >= 30) {
          recordStep(STEP.发起支付, STATE.超时, '未获取支付结果，计时已超时');
          clearInterval(session.timer);
        }
        session.counter++;
      }, 1000);

      recordStep(STEP.发起支付, STATE.处理中, { msg: '开始30s超时计时', paymentToken });

      toApplePay({
        paymentData: paymentToken.paymentData,
        orderNumber,
        payMode,
      })
        .then((res) => {
          clearInterval(session.timer);
          // 判断成功或失败要重新查一次订单状态，不能通过res code 来判断
          orderState.getSimpleOrderInfo({
            orderNumber: res.data.orderNumber,
            callback: ({ data: orderInfo }) => {
              let paySuccess = orderInfo?.simpleOrderInfo?.orderState === ORDER_STATE.已支付;

              // 支付成功，提示成功, 并跳转支付结果页
              if (paySuccess) {
                recordStep(STEP.发起支付, STATE.成功, { msg: '支付成功', res });
                session.completePayment({ status: window.ApplePaySession.STATUS_SUCCESS });
                browserPushUrl(`/order/result/${encodeURIEncryptFE(orderNumber)}`);
              }

              // 需要abort来终止会话(不会触发cancel)
              if (!paySuccess) {
                session.abort();
                recordStep(STEP.发起支付, STATE.失败, { msg: '支付失败', res });
                if (isOrderConfirmPage) {
                  browserPushUrl(`/order/result/${encodeURIEncryptFE(orderNumber)}`);
                } else {
                  message.info(LANG['payments.methods.applePay_exception']);
                  orderState.setStateValue('_beingPaid', false);
                }
              }
            },
          });
        })
        .catch((err) => {
          clearInterval(session.timer);
          recordStep(STEP.发起支付, STATE.异常, { msg: '支付异常', err });
          session.oncancel();
          if (isOrderConfirmPage) {
            browserPushUrl(`/order/result/${encodeURIEncryptFE(orderNumber)}`);
          }
        });
    };

    session.oncancel = (event) => handleCancel({ isOrderConfirmPage, orderNumber, orderState, event });
  } catch (catchError) {
    handleException({ isOrderConfirmPage, orderState, orderNumber, catchError, from: 'wakeUpPayment', LANG });
  }
};

/**
 *
 * @description 封装request，创建会话
 * @date 2024/01/04 10:52:52
 * @param countryCode {String} 二字码
 * @param currencyCode {String} 币种
 * @param totalAmount {Number} 总金额, 等于小计 + 运费 + 税费 - 优惠
 * @param balancePrice {Number} 商品金额小计，subtotal
 * @param shippingFee {Number} 运费
 * @param couponFee {Number} 优惠，没有则不显示
 * @param taxFee {Number} 税费，不同地区不同文案，没有则不显示
 * @param LANG {Number} 多语言文案
 *
 */
const requestSession = (params) => {
  let {
    countryCode,
    currencyCode,
    totalAmount = 0,
    balancePrice = 0,
    shippingFee = 0,
    couponFee = 0,
    taxFee = 0,
    LANG,
    orderState,
    isOrderConfirmPage,
  } = params;
  return new Promise((resolve) => {
    let request = null;
    window.InstaApplePaySession = null;
    try {
      couponFee = Math.abs(couponFee);

      // 日韩需要取整
      if (['JP', 'KR'].includes(countryCode)) {
        totalAmount = parseInt(totalAmount);
        balancePrice = parseInt(balancePrice);
        shippingFee = parseInt(shippingFee);
        couponFee = parseInt(couponFee);
        taxFee = parseInt(taxFee);
      }

      request = {
        countryCode,
        currencyCode,
        merchantCapabilities: ['supports3DS'],
        supportedNetworks: ['visa', 'masterCard'], // 'amex', 'discover'
        total: {
          label: 'Insta360',
          type: 'final',
          amount: totalAmount,
        },
        lineItems: [
          {
            label: LANG['common.label.balancePrice'], // 商品总价
            amount: balancePrice,
          },
          {
            label: LANG['common.label.shippingCost'], // 运费
            amount: shippingFee,
          },
        ],
      };

      // 从订单信息中获取额外订单支付数据
      const lineItemExtra = {
        couponFee: {
          label: LANG['common.label.couponCode'],
          amount: -1 * couponFee,
        },
        taxFee: {
          label: LANG['common.label.balanceTax'],
          amount: taxFee,
        },
      };
      // 如果有折扣
      if (couponFee > 0) request.lineItems.push(lineItemExtra.couponFee);
      // 如果有税费
      if (taxFee > 0) {
        // 不同地区展示不同税费文案
        switch (countryCode) {
          case 'GR':
            lineItemExtra.taxFee.label = 'Custom Service Fee';
            break;
          case 'CA':
            lineItemExtra.taxFee.label = 'Import Tax';
            break;
          case 'US':
            lineItemExtra.taxFee.label = 'Estimated Sales Tax';
            break;
          default:
        }
        request.lineItems.push(lineItemExtra.taxFee);
      }

      window.InstaApplePaySession = new window.ApplePaySession(10, request);
      window.InstaApplePaySession.countryCode = countryCode;
      window.InstaApplePaySession.LANG = LANG;
      recordStep(STEP.创建会话, STATE.成功, request);
      resolve();
    } catch (catchError) {
      // 创建会话出现任何异常，中断订单创建，并提示用户选择其他支付方式。

      handleException({
        isOrderConfirmPage,
        orderState,
        catchError,
        catchErrorData: { countryCode, currencyCode, totalAmount, balancePrice, shippingFee, couponFee, taxFee },
        from: 'requestSession',
        LANG,
      });
    }
  });
};

export default {
  enable,
  requestSession,
  wakeUpPayment,
};
